import React, { useEffect, useRef, useState } from 'react';
import Localize from 'react-intl-universal';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

//MUI
import LoginIcon from '@mui/icons-material/Login';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import * as commonConstants from '@common/Constants.js';
import { createSignUpConfirmService } from '@common/Helper.js';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import useFetching from '@hooks/Fetching/Fetching.js';
import * as constants from '@navigation/Constants.js';
import SignUpForm from '@pages/LogIn/SignUpForm/SignUpForm';
import tokenUtil from '@services/tokenUtil';

import LoginForm from './LoginForm/LoginForm';
import { createLoginService } from './util/Util.js';

const StyledContainerBox = styled(Box)(() => ({
  minHeight: 'calc(100vh - 5.4rem)',
  display: 'flex'
}));
const StyledContainerGrid = styled(Grid)(() => ({
  margin: 0
}));

const StyledImgColumn = styled(Grid)((props) => ({
  width: '100%',
  height: 'auto',
  backgroundImage: `url(${require('@assets/Images/' + props?.configuration?.login?.backgroundImageName || 'signupBig.jpg')})`,
  backgroundPosition: 'top',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover'
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  paddingBottom: '1rem'
}));

const StyledDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  paddingBottom: '1rem',
  [theme.breakpoints.down('md')]: {
    visibility: 'hidden'
  }
}));
const StyledWelcomeGrid = styled(Grid)(({ theme }) => ({
  padding: '3rem',
  color: theme.palette.primary.contrastText
}));
const StyledKnowledgeGrid = styled(Grid)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  borderBottom: `1px solid ${theme.palette.primary.contrastText}`,
  marginBottom: '1rem',
  marginLeft: '3rem',
  [theme.breakpoints.down('md')]: {
    visibility: 'hidden'
  }
}));
const StyledRectangle = styled(Box)(({ theme }) => ({
  width: '4rem',
  height: '4rem',
  border: `1px solid ${theme.palette?.primary?.contrastText}`,
  transform: 'rotate(45deg)',
  margin: '1rem',
  [theme.breakpoints.down('md')]: {
    visibility: 'hidden'
  }
}));
const StyledFormColumn = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette?.login?.background,
  padding: '4rem !important',
  textAlign: 'center',
  maxHeight: 'calc(100vh - 5.5rem)',
  overflowY: 'auto'
}));
const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  fontWeight: '700',
  width: '70%',
  fontSize: '14px',
  marginBottom: '1rem',
  textTransform: 'none',
  borderRadius: '1',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  '&.MuiLoadingButton-loading': {
    width: '12rem'
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.light
  }
}));

const LogIn = (props) => {
  const { configuration } = props;
  const { email, confirmationCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [locationData, setLocationData] = useState(location.key);
  const theme = useTheme();

  const prevLocationData = useRef(locationData);
  const [userData, setUserData] = useState(null);
  const [contextData, setContextData] = useGlobalContext();
  const [confCode, setConfCode] = useState('');
  const [userEmail, setUserEmail] = useState(
    contextData?.userData?.email ? contextData.userData.email : ''
  );

  const [isSignUp, setIsSignUp] = useState(false);
  const [activationEnabled, setActivationEnabled] = useState(false);
  const [, setResponseMessage] = useState(
    location?.state?.data ? location.state.data : contextData?.userMsg ? contextData.userMsg : null
  );

  const callbackServiceCall = (response) => {
    setResponseMessage(response.data);
  };

  const loginAreaVisibility = configuration?.global?.loginAreaVisibility;

  const loginServiceCallbackSuccess = (response) => {
    const newData = { ...contextData };
    newData.loggedIn = true;
    newData.email = response.data.email;
    setUserData(response.data.email);
  };

  const loginServiceCallbackFail = (response) => {
    setResponseMessage(response.data);
    //TODO --- remove this when Auth BE task will finish
    setUserData('tobias.rothley@contrimo.com');
    tokenUtil.setUser({ email: 'tobias.rothley@contrimo.com' });
  };

  let previousPathName = location?.state?.from
    ? location.state.from.includes('/')
      ? location.state.from
      : location.state.from + ' /'
    : '';
  let previousPath = location?.state?.path ? location.state.path : '';

  const [, , , confirmSignUpUser] = useFetching(
    createSignUpConfirmService.bind(null, callbackServiceCall, confCode, userEmail),
    true
  );

  if (
    previousPath.includes(constants.routes.path.login) ||
    previousPath.includes(constants.routes.path.verify)
  ) {
    previousPath = '';
    previousPathName = '';
  }

  const handleSSO = (e) => {
    switch (e.target.name) {
      case 'loginWithSapIasSSO':
        break;
      case 'loginWithKeyClockSSO':
        break;
      default:
    }
  };

  useEffect(() => {
    if (prevLocationData.current !== location.key) {
      setIsSignUp(false);
      setLocationData(location.key);
    }
    prevLocationData.current = locationData;
  }, [location]);

  useEffect(() => {
    if (email && email !== commonConstants.confirmationCodes.confirm) {
      setUserEmail(email);
    }

    if (confirmationCode && confirmationCode !== commonConstants.confirmationCodes.confirm) {
      setConfCode(confirmationCode);
      setActivationEnabled(true);
    }
  }, [email, confirmationCode]);

  useEffect(() => {
    if (confCode && activationEnabled) {
      confirmSignUpUser();
    }
  }, [activationEnabled, confCode, confirmSignUpUser]);

  useEffect(() => {
    if (userData) {
      let newContext = { ...contextData };
      delete newContext.userMsg;
      setContextData({
        ...newContext,
        user: userData,
        loggedIn: true
      });
      if (previousPath && !previousPath.includes('home')) {
        navigate(previousPath, {
          state: {
            from: previousPathName.replace('/', '').trim(),
            path: previousPath
          }
        });
      } else {
        navigate(constants.routes.path.myarea, {
          state: {
            from: 'Home',
            path: '/'
          }
        });
      }
    }
  }, [userData]);

  const gridLeftWidth = loginAreaVisibility ? 7 : 9;

  const onSubmit = async (values) => {
    if (theme.haveLogin) {
      await createLoginService(loginServiceCallbackSuccess, loginServiceCallbackFail, values);
    }
  };

  return (
    <StyledContainerBox>
      <StyledContainerGrid container spacing={1}>
        <StyledImgColumn
          item
          sm={12}
          lg={loginAreaVisibility ? 9 : 12}
          configuration={configuration}
        >
          <Grid sx={{ paddingLeft: '4rem', textAlign: 'left' }}>
            <StyledWelcomeGrid item lg={gridLeftWidth} sm={12}>
              <StyledTypography variant='h5'>{Localize.get('SignIn.welcome')}</StyledTypography>
              {Localize.get('SignIn.orbisMea')}
            </StyledWelcomeGrid>
            <StyledKnowledgeGrid item lg={6} sm={12}>
              <Typography sx={{ fontSize: '18px', fontWeight: '700' }}>
                {Localize.get('HomePage.benefitKnowledge')}
              </Typography>
            </StyledKnowledgeGrid>
            <Grid container item>
              <Grid
                item
                lg={2}
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <StyledRectangle />
              </Grid>
              <Grid
                item
                lg={4}
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <StyledDescription> {Localize.get('HomePage.rightDescription')}</StyledDescription>
              </Grid>
            </Grid>
          </Grid>
        </StyledImgColumn>
        <StyledFormColumn item sm={12} lg={3}>
          {isSignUp ? (
            <SignUpForm />
          ) : (
            <>
              <Box
                component='img'
                src={require(`../../assets/Images/${theme?.loginLogoName}`)}
                alt='Logo Image'
                sx={{
                  width: '10rem',
                  marginTop: '4rem',
                  marginBottom: '1rem'
                }}
              />
              <LoginForm handleSubmit={onSubmit} setIsSignUp={setIsSignUp} />
              <Divider sx={{ margin: '1rem 0' }}>{Localize.get('SignIn.continueWith')}</Divider>
              <Box sx={{ textAlign: 'center' }}>
                <StyledLoadingButton
                  name='loginWithSapIasSSO'
                  onClick={handleSSO}
                  data-testid={'loginWithSapIasSSO'}
                  endIcon={<LoginIcon />}
                >
                  {Localize.get('SignIn.loginWithSapIasSSO')}
                </StyledLoadingButton>
                <StyledLoadingButton
                  name='loginWithKeyClockSSO'
                  onClick={handleSSO}
                  endIcon={<LoginIcon />}
                >
                  {Localize.get('SignIn.loginWithKeyClockSSO')}
                </StyledLoadingButton>
              </Box>
            </>
          )}
        </StyledFormColumn>
      </StyledContainerGrid>
    </StyledContainerBox>
  );
};
export default LogIn;
